import { format } from "date-fns";
import { numericFormatter } from "react-number-format";
import { useNavigate } from "react-router-dom";
import { keycloak } from "../../../lib/keycloak";
import { EditIcon } from "../../../ui/icons/edit";
import { SortIcon } from "../../../ui/icons/sort";
import { TrashIcon } from "../../../ui/icons/trash";
import Avatar from "../../../ui/molecules/avatar";
import Pills from "../../../ui/molecules/pills";
import { PillsColor } from "../../../ui/molecules/pills/Pills.types";
import { Dropdown } from "../../../ui/organisms/dropdown";
import { CustomTable } from "../../../ui/organisms/table";
import { UserDTO } from "../../users/dto";
import { FindAllProjectsFilters, FindAllProjectsResponse, ProjectStateEnum } from "../dto";

interface Props {
    setIdToDelete: (arg?: string) => void
    projects: FindAllProjectsResponse
    filters: FindAllProjectsFilters
    setFilters: (filters: Partial<FindAllProjectsFilters>) => void
    teamLeaders: { user: UserDTO, avatar: string }[]
    commercials: { user: UserDTO, avatar: string }[]
}

export function ProjectTable(props: Props) {
    const { setIdToDelete, projects, filters, setFilters, teamLeaders, commercials } = props
    const navigate = useNavigate();

    const projectStateMap = new Map<
        ProjectStateEnum,
        { label: string; color: PillsColor }
    >([
        [ProjectStateEnum.TOSTART, { label: "Da iniziare", color: "gray" }],
        [ProjectStateEnum.CONCLUDED, { label: "Concluso", color: "green" }],
        [ProjectStateEnum.INPROGRESS, { label: "In corso", color: "blue" }],
        [ProjectStateEnum.SUSPENDED, { label: "Sospeso", color: "orange" }],
    ]);

    return (
        <CustomTable
            asc={filters.order}
            settedOrder={filters.sort}
            tableHeight='calc(100% - 48px)'
            prevOrderHeads={[
                <th className="text-left">
                    <div className="flex gap-[4px]">
                        Nome progetto
                        <div
                            onClick={() => setFilters({ ...filters, sort: "name", order: !filters.order })}
                            className="cursor-pointer hover: opacity-70"
                        >
                            <SortIcon />
                        </div>
                    </div>
                </th>,
                <th className="text-left">
                    <div className="flex gap-[4px]">
                        Settore
                        <div
                            onClick={() => setFilters({ ...filters, sort: "customer.companySector", order: !filters.order })}
                            className="cursor-pointer hover: opacity-70"
                        >
                            <SortIcon />
                        </div>
                    </div>
                </th>,
                <th className="text-left">
                    <div className="flex gap-[4px]">
                        Team Leader
                        <div
                            onClick={() => setFilters({ ...filters, sort: "teamLeader.name", order: !filters.order })}
                            className="cursor-pointer hover: opacity-70"
                        >
                            <SortIcon />
                        </div>
                    </div>
                </th>,
                <th className="text-center">
                    <div className="flex gap-[4px]">
                        Commerciali
                        <div
                            onClick={() => setFilters({ ...filters, sort: "commercials.name", order: !filters.order })}
                            className="cursor-pointer hover: opacity-70"
                        >
                            <SortIcon />
                        </div>
                    </div>
                </th>,
                <th className="text-left">
                    <div className="flex gap-[4px]">
                        Data inizio
                        <div
                            onClick={() => setFilters({ ...filters, sort: "startDate", order: !filters.order })}
                            className="cursor-pointer hover: opacity-70"
                        >
                            <SortIcon />
                        </div>
                    </div>
                </th>,
                <th className="text-left">
                    <div className="flex gap-[4px]">
                        Data fine
                        <div
                            onClick={() => setFilters({ ...filters, sort: "endDate", order: !filters.order })}
                            className="cursor-pointer hover: opacity-70"
                        >
                            <SortIcon />
                        </div>
                    </div>
                </th>,
                <th className="text-left">
                    <div className="flex gap-[4px]">
                        Stato
                        <div
                            onClick={() => setFilters({ ...filters, sort: "state", order: !filters.order })}
                            className="cursor-pointer hover: opacity-70"
                        >
                            <SortIcon />
                        </div>
                    </div>
                </th>,
                <th className="text-left">
                    <div className="flex gap-[4px]">
                        Liste target
                        <div
                            onClick={() => setFilters({ ...filters, sort: "targetListSize", order: !filters.order })}
                            className="cursor-pointer hover: opacity-70"
                        >
                            <SortIcon />
                        </div>
                    </div>
                </th>,
                <th className="text-left">
                    <div className="flex gap-[4px]">
                        Contatti
                        <div
                            onClick={() => setFilters({ ...filters, sort: "contactsNumber", order: !filters.order })}
                            className="cursor-pointer hover: opacity-70"
                        >
                            <SortIcon />
                        </div>
                    </div>
                </th>,
                <th className="text-left">
                    <div className="flex gap-[4px] w-[20px]" />
                </th>
            ]}
            postOrderHeads={[]}
            preferences={[]}
            setPreferences={(items) => { }}
            setOrderFilters={(order) => { }}
            rows={projects?.data.map(d => {
                const teamLeader = teamLeaders.find(tl => tl.user.id === d.teamLeaderId)
                const projectCommercials = commercials.filter((c) => d.commercialIds?.includes(c.user.id));
                return ({
                    key: d.id, data: [
                        {
                            item: <td
                                onClick={() => navigate('/edit-project/' + d.id + '/specifics')}
                                className="max-w-[200px] cursor-pointer crop-text text-text-sm font-semibold text-neutral-600">
                                {d.name}
                            </td>,
                            group: 'preSort',
                            key: 'checkbox'
                        },
                        {
                            item: <td className="max-w-[200px]">
                                {d.sector ? (
                                    <Pills
                                        size={"xs"}
                                        label={d.sector}
                                        outline={false}
                                        emphasis={false}
                                        color={"blue"}
                                    />
                                ) : (
                                    ""
                                )}
                            </td>,
                            group: 'preSort',
                            key: 'Nome referente'
                        },
                        {
                            item: <td>
                                <div className="flex items-center gap-2">
                                    <Avatar
                                        type="single"
                                        size="sm"
                                        shape="circle"
                                        imageUrl={teamLeader?.avatar ?? ""}
                                        fullName={teamLeader?.user.name && teamLeader?.user.surname ? (teamLeader.user.name + " " + teamLeader.user.surname) : ""}
                                        altTextInitials={
                                            teamLeader?.user.name && teamLeader?.user.surname ?
                                                (teamLeader?.user.name[0] +
                                                    teamLeader?.user.surname[0]) : ""
                                        }
                                    />
                                    {teamLeader?.user.name && teamLeader?.user.surname ? (teamLeader.user.name + " " + teamLeader.user.surname) : ""}
                                </div>
                            </td>,
                            group: 'preSort',
                            key: 'Cognome referente'
                        },
                        {
                            item: <td>
                                <Avatar
                                    type="group"
                                    size="sm"
                                    length={2}
                                    imageUrls={projectCommercials.map((commercial) => ({
                                        altTextInitials: commercial.user.name[0] + commercial.user.surname[0],
                                        fullNames: (commercial.user.name + " " + commercial.user.surname),
                                        url: commercial.avatar ?? "",
                                    }))}
                                />
                            </td>,
                            group: 'preSort',
                            key: 'Linkedin'
                        },
                        {
                            item: <td className="max-w-[150px] crop-text text-text-sm font-semibold text-neutral-600">
                                {d.startDate
                                    ? format(new Date(d.startDate), "dd/MM/yyyy")
                                    : ""}
                            </td>,
                            group: 'preSort',
                            key: 'Ruolo'
                        },
                        {
                            item: <td className="max-w-[150px] crop-text text-text-sm font-semibold text-neutral-600">
                                {d.endDate
                                    ? format(new Date(d.endDate), "dd/MM/yyyy")
                                    : ""}
                            </td>,
                            group: 'preSort',
                            key: 'Data di contatto'
                        },
                        {
                            item: <td>
                                <Pills
                                    size={"xs"}
                                    label={projectStateMap.get(d.state)?.label!}
                                    outline={false}
                                    emphasis={false}
                                    color={projectStateMap.get(d.state)?.color!}
                                />
                            </td>,
                            group: 'preSort',
                            key: 'Esito'
                        },
                        {
                            item: <td>
                                <Pills
                                    size={"xs"}
                                    label={numericFormatter(d.targetListsIds?.length.toString() ?? "0", { thousandSeparator: '.' })}
                                    outline={false}
                                    emphasis={false}
                                    color={"blue"}
                                />
                            </td>,
                            group: 'preSort',
                            key: 'Note'
                        },
                        {
                            item: <td>
                                <Pills
                                    size={"xs"}
                                    label={numericFormatter(d.contactsNumber?.toString() ?? "0", { thousandSeparator: '.' })}
                                    outline={false}
                                    emphasis={false}
                                    color={"blue"}
                                />
                            </td>,
                            group: 'preSort',
                            key: 'Smartphone'
                        },
                        {
                            item: <td className="w-[20px] cursor-pointer">
                                <Dropdown
                                    items={[
                                        {
                                            label: "Modifica",
                                            icon: <EditIcon color={""} size={0} />,
                                            onClick: () => navigate('/edit-project/' + d.id + '/specifics'),
                                        },
                                        (
                                            keycloak.hasRealmRole('customer') ||
                                                keycloak.hasRealmRole('collaborator')
                                                ? null : {
                                                    label: "Cancella",
                                                    icon: <TrashIcon color={""} size={0} />,
                                                    onClick: () => setIdToDelete(d.id),
                                                }),
                                    ]}
                                />
                            </td>,
                            group: 'preSort',
                            key: 'Email'
                        },
                    ]
                })
            }) ?? []}
        />
    )
}